import type { CaptureResult, PostHog } from 'posthog-js'

/**
 * Keeps credentials that arrive in the page URL out of PostHog.
 *
 * Copy of `apps/frontend/app/analytics/url-credentials.ts`: the two apps share
 * no package and do not import from each other, so each carries the rule. The
 * dashboard's Supabase client also uses the implicit flow, and a magic link or
 * OAuth return that lands here carries the same fragment.
 *
 * Where they come from: Supabase's implicit flow returns
 * `#access_token=…&refresh_token=…` to the redirect URL of an OAuth or magic-link
 * sign-in; a PKCE or email link carries `?code=` or `?token_hash=`; customer-app
 * links that reach this origin carry `?token=` or `?join_token=`, possibly
 * nested in another parameter such as a login redirect. The SDK copies
 * `location.href` into `$current_url`, `$session_entry_url`, the heatmap
 * buckets, the replay meta events and the stored first-seen URL
 * (`$initial_person_info`, in a cookie and in every `/flags` request).
 *
 * The rule: remove credential parameters from a query string one by one, so
 * UTM, click-id and ordinary parameters survive, and clean the value of every
 * kept parameter the same way, percent-encoded or not; remove a fragment that
 * carries one as a whole, because such a fragment is a Supabase session
 * hand-off and the rest of it is session metadata (`expires_at`, `token_type`,
 * `type`). Error fragments (`#error=…`) and plain anchors stay. Names match
 * case-insensitively.
 */
const CREDENTIAL_PARAMS = [
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'code',
  'token_hash',
  'token',
  'join_token',
] as const

const CREDENTIAL_PARAM_SET: ReadonlySet<string> = new Set(CREDENTIAL_PARAMS)
const NAMES = CREDENTIAL_PARAMS.join('|')
// `%3F`, `%253F`, …: a delimiter percent-encoded once or more, as it appears
// when a URL is nested in another parameter's value.
const encoded = (hex: string) => `%(?:25)*${hex}`
const MAY_CARRY_CREDENTIAL = new RegExp(
  `(?:[?#&]|${encoded('3F')}|${encoded('23')}|${encoded('26')})(?:${NAMES})(?:=|${encoded('3D')})`,
  'i',
)
// Credential values are base64url, hex or UUIDs, so `,`, `)` or `]` after one
// ends it and the URL around it: `(https://…?token=abc)`, `…?token=a,https://…`.
const CREDENTIAL_VALUE_THEN_END = new RegExp(`[?#&](?:${NAMES})=[^&#,)\\]]*(?=[,)\\]])`, 'i')
// A URL inside a longer string ends at whitespace or a quote: an exception
// message, or an autocapture `$elements_chain` (`attr__href="…"`).
const URL_LIKE_RUN = /[^\s"'<>]+/g
const MAX_NESTING = 4

function isCredentialPair(pair: string): boolean {
  return CREDENTIAL_PARAM_SET.has((pair.split('=', 1)[0] ?? '').toLowerCase())
}

function decodeFully(raw: string): { text: string, times: number } {
  let text = raw
  let times = 0
  while (times < MAX_NESTING && /%[0-9a-f]{2}/i.test(text)) {
    try {
      const next = decodeURIComponent(text)
      if (next === text) break
      text = next
      times++
    } catch {
      break
    }
  }
  return { text, times }
}

/** Cleans the value of a kept `key=value` pair, which may hold a nested URL. */
function scrubPairValue(pair: string, depth: number): string {
  const eq = pair.indexOf('=')
  if (eq === -1) return pair
  const raw = pair.slice(eq + 1)
  if (!MAY_CARRY_CREDENTIAL.test(raw)) return pair
  const { text, times } = decodeFully(raw)
  const cleaned = stripUrlCredentials(text, depth + 1)
  if (cleaned === text) return pair
  let value = cleaned
  for (let i = 0; i < times; i++) value = encodeURIComponent(value)
  return `${pair.slice(0, eq + 1)}${value}`
}

function stripOneUrl(url: string, depth: number): string {
  const end = CREDENTIAL_VALUE_THEN_END.exec(url)
  if (end) {
    const cut = end.index + end[0].length
    return stripOneUrl(url.slice(0, cut), depth) + stripUrlCredentials(url.slice(cut), depth)
  }
  const hashAt = url.indexOf('#')
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt)
  let fragment = hashAt === -1 ? '' : url.slice(hashAt)
  const queryAt = beforeHash.indexOf('?')
  const base = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt)
  const query = queryAt === -1
    ? ''
    : beforeHash.slice(queryAt + 1).split('&')
      .filter(pair => !isCredentialPair(pair))
      .map(pair => scrubPairValue(pair, depth))
      .join('&')
  if (fragment.slice(1).split(/[&?]/).some(isCredentialPair)) fragment = ''
  else if (fragment) fragment = `#${fragment.slice(1).replace(/[^&?]+/g, pair => scrubPairValue(pair, depth))}`
  return `${base}${query ? `?${query}` : ''}${fragment}`
}

/**
 * Returns `value` with credential parameters removed from every URL in it.
 * Works on a bare URL (`$current_url`) and on text that embeds one; a string
 * without a credential parameter comes back as is.
 */
export function stripUrlCredentials(value: string, depth = 0): string {
  if (depth > MAX_NESTING || !MAY_CARRY_CREDENTIAL.test(value)) return value
  return value.replace(URL_LIKE_RUN, run => (MAY_CARRY_CREDENTIAL.test(run) ? stripOneUrl(run, depth) : run))
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]'
}

/**
 * Copy-on-write walk: returns the same reference when nothing changed and never
 * mutates its input. A reference back to an object still being walked is
 * returned as is, so a cycle cannot recurse forever.
 */
function scrub(value: unknown, walking: WeakSet<object> = new WeakSet()): unknown {
  if (typeof value === 'string') return stripUrlCredentials(value)
  if (!Array.isArray(value) && !isPlainObject(value)) return value
  if (walking.has(value)) return value
  walking.add(value)
  try {
    if (Array.isArray(value)) {
      let changed = false
      const next = value.map((item) => {
        const scrubbed = scrub(item, walking)
        if (scrubbed !== item) changed = true
        return scrubbed
      })
      return changed ? next : value
    }

    let changed = false
    const next: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
      const scrubbed = scrub(item, walking)
      // Heatmap data is keyed by page URL, so keys are URLs too. Two keys that
      // differ only by a credential collapse into one: their arrays (heatmap
      // buckets) are joined; for any other value the first one is kept.
      const cleanKey = stripUrlCredentials(key)
      if (scrubbed !== item || cleanKey !== key) changed = true
      if (!(cleanKey in next)) next[cleanKey] = scrubbed
      else if (Array.isArray(next[cleanKey]) && Array.isArray(scrubbed)) {
        next[cleanKey] = [...(next[cleanKey] as unknown[]), ...scrubbed]
      }
    }
    return changed ? next : value
  } finally {
    walking.delete(value)
  }
}

// rrweb event types whose payload is plain JSON that can hold the page URL:
// Meta (`data.href`), Custom (`$url_changed`, `$pageview` hrefs) and Plugin
// (network-timing entry names). Full and incremental snapshots are DOM data,
// partly gzipped by the recorder, and are left untouched.
const REPLAY_URL_EVENT_TYPES: ReadonlySet<unknown> = new Set([4, 5, 6])

function scrubSnapshotData(data: unknown): unknown {
  if (!Array.isArray(data)) return data
  let changed = false
  const next = data.map((event: unknown) => {
    if (!isPlainObject(event) || !REPLAY_URL_EVENT_TYPES.has(event.type)) return event
    const scrubbed = scrub(event)
    if (scrubbed !== event) changed = true
    return scrubbed
  })
  return changed ? next : data
}

function scrubEvent(event: CaptureResult): CaptureResult {
  const properties = event.event === '$snapshot'
    ? { ...event.properties, $snapshot_data: scrubSnapshotData(event.properties.$snapshot_data) }
    : scrub(event.properties) as CaptureResult['properties']
  const result: CaptureResult = { ...event, properties }
  if (event.$set) result.$set = scrub(event.$set) as CaptureResult['$set']
  if (event.$set_once) result.$set_once = scrub(event.$set_once) as CaptureResult['$set_once']
  return result
}

/**
 * PostHog `before_send` hook: every event the SDK sends passes here, replay
 * `$snapshot` batches included. It changes nothing but URL credentials and
 * never drops an event: the SDK drops one whose hook throws, so on an
 * unexpected shape (a throwing getter) the event goes out unchanged.
 */
export function scrubPostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  try {
    return scrubEvent(event)
  } catch {
    return event
  }
}

// Stored values that hold `location.href`: the first-seen URL (`{ r, u }`,
// persisted in the `ph_<key>_posthog` cookie and localStorage, read into every
// `/flags` request) and the session-entry URL (sessionStorage).
const STORED_URL_KEYS = ['$initial_person_info', '$client_session_props'] as const

// The instance `loaded` receives is typed as the public interface, which does
// not declare the persistence stores the SDK instance carries.
type Stores = Pick<PostHog, 'persistence' | 'sessionPersistence'>

function scrubStore(store: PostHog['persistence']): void {
  if (!store) return
  for (const key of STORED_URL_KEYS) {
    const value = store.props[key]
    const cleaned = scrub(value)
    if (cleaned !== value) store.register({ [key]: cleaned })
  }
}

/**
 * PostHog `loaded` hook: rewrites the URLs the SDK stores, which `before_send`
 * never sees. It runs once at load (a value stored by an earlier visit) and
 * after every capture, because the first capture on a page is what stores the
 * first-seen URL, and it does so synchronously, before a `/flags` request can
 * read it.
 */
export function scrubStoredUrls(client: Pick<PostHog, 'on'>): void {
  const stores = client as Partial<Stores>
  const run = () => {
    try {
      scrubStore(stores.persistence)
      scrubStore(stores.sessionPersistence)
    } catch {
      // Storage unavailable: nothing was stored to rewrite.
    }
  }
  run()
  client.on('eventCaptured', run)
}
